import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { LoginModel } from 'src/app/model/login.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SnackBarService } from 'src/app/services/snack-bar-service.service';
import { IFiltroMotoristaModel, IMotoristaModel } from 'src/app/motorista/model/motorista.model';
import { MotoristaService } from 'src/app/motorista/motorista.service';
import {
  isColunaPesquisaMotoristaAutorizacaoManual,
  MOTORISTA_PESQUISA_AM_COLUNA,
  MOTORISTA_PESQUISA_AM_OPERADOR_CONTENDO,
  prepararFiltroMotoristaParaApi,
} from './motorista-pesquisa-autorizacao-manual.config';

/**
 * Diálogo de seleção de motoristas para Autorização manual (MOBILIDADE-2922).
 * Colunas e filtros distintos do {@link PesquisaMotoristaComponent} usado em registro de transações.
 */
@Component({
  selector: 'app-pesquisa-motorista-autorizacao-manual',
  templateUrl: './pesquisa-motorista-autorizacao-manual.component.html',
  styleUrls: ['./pesquisa-motorista-autorizacao-manual.component.scss'],
})
export class PesquisaMotoristaAutorizacaoManualComponent implements OnInit, OnDestroy {
  usuario!: LoginModel | undefined;

  private subs = new Subscription();

  readonly opcoesColunaPesquisa: { value: string; label: string }[] = [
    { value: MOTORISTA_PESQUISA_AM_COLUNA.nome, label: 'Nome' },
    { value: MOTORISTA_PESQUISA_AM_COLUNA.apelido, label: 'Apelido' },
    { value: MOTORISTA_PESQUISA_AM_COLUNA.matricula, label: 'Matrícula' },
    { value: MOTORISTA_PESQUISA_AM_COLUNA.cpf, label: 'CPF' },
    { value: MOTORISTA_PESQUISA_AM_COLUNA.celular, label: 'Celular' },
  ];

  displayedColumns = ['nome', 'apelido', 'cpf', 'matricula', 'celular'];

  filtro!: IFiltroMotoristaModel;

  public dataSource!: MatTableDataSource<IMotoristaModel>;

  @ViewChild('search', {}) parametroElement!: ElementRef<HTMLInputElement>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = 0;

  constructor(
    private motoristaService: MotoristaService,
    private authenticationService: AuthenticationService,
    private snackBar: SnackBarService,
    private dialogRef: MatDialogRef<PesquisaMotoristaAutorizacaoManualComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dataKey: IFiltroMotoristaModel },
  ) {
    this.filtro = data.dataKey;
  }

  ngOnInit(): void {
    this.garantirFiltroPadrao();
    this.loading++;
    this.usuario = this.motoristaService?.usuario;

    this.authenticationService
      .validate(this.motoristaService?.usuario?.retorno?.guid ?? '')
      .then((dados) => {
        if (!dados.sucesso) {
          this.snackBar.flashMessage(
            this.authenticationService.msgSessaoExpirada,
            ['snackbar-error'],
            'Fechar',
            0,
          );
          this.usuario = undefined;
        } else {
          this.filtrar();
        }
      })
      .catch(() => {
        this.snackBar.flashMessage(
          this.authenticationService.msgSessaoExpirada,
          ['snackbar-error'],
          'Fechar',
          0,
        );
      })
      .finally(() => {
        this.loading--;
      });
  }

  formatCelular(row: IMotoristaModel): string {
    const ddd = (row.numDddCelular || '').toString().replace(/\D/g, '');
    const num = (row.numCelular || '').toString().replace(/\D/g, '');
    if (!ddd && !num) {
      return '';
    }
    if (ddd && num) {
      return `${ddd} ${num}`;
    }
    return ddd || num;
  }

  applyFilter(filterValue: string): void {
    const v = filterValue.trim().toLowerCase();
    this.dataSource.filter = v;
  }

  onSubmit(): void {
    this.filtrar();
  }

  limpaFiltro(): void {
    this.garantirFiltroPadrao();
    this.filtro.parametro = '';
    this.dataSource = new MatTableDataSource<IMotoristaModel>([]);
    this.applyDataSourceHelpers(this.dataSource);
  }

  fechar(): void {
    this.dialogRef.close();
  }

  getRecord(row: IMotoristaModel): void {
    this.dialogRef.close({ data: row });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyDataSourceHelpers(ds: MatTableDataSource<IMotoristaModel>): void {
    ds.filterPredicate = (data: IMotoristaModel, filter: string) => {
      const f = filter.trim().toLowerCase();
      if (!f) {
        return true;
      }
      const cpf = (data.cpf || '').toString().replace(/\D/g, '');
      const blob = [
        data.nome,
        data.apelido,
        data.matricula,
        cpf,
        this.formatCelular(data).replace(/\D/g, ''),
        this.formatCelular(data),
        String(data.codigo ?? ''),
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(f);
    };

    ds.sortingDataAccessor = (data: IMotoristaModel, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'cpf':
          return (data.cpf || '').toString().replace(/\D/g, '');
        case 'celular':
          return this.formatCelular(data).replace(/\D/g, '');
        default: {
          const v = (data as unknown as Record<string, unknown>)[sortHeaderId];
          return v != null ? String(v) : '';
        }
      }
    };
  }

  private garantirFiltroPadrao(): void {
    if (!isColunaPesquisaMotoristaAutorizacaoManual(this.filtro.coluna)) {
      this.filtro.coluna = MOTORISTA_PESQUISA_AM_COLUNA.nome;
    }
    if (this.filtro.operador == null) {
      this.filtro.operador = MOTORISTA_PESQUISA_AM_OPERADOR_CONTENDO;
    }
  }

  private filtrar(): void {
    this.loading++;
    const payload = prepararFiltroMotoristaParaApi(this.filtro);
    payload.frota = { codigo: this.motoristaService?.usuario?.retorno?.codigoEntidade };
    this.subs.add(
      this.motoristaService.listMotorista(payload).subscribe({
        next: (res) => {
          if (res?.sucesso) {
            this.dataSource = new MatTableDataSource<IMotoristaModel>(res.retorno ?? []);
            this.applyDataSourceHelpers(this.dataSource);
            this.dataSource.paginator = this.paginator;
            this.sort.sort({ id: 'nome', start: 'asc' } as MatSortable);
            this.sort.disableClear = true;
            this.dataSource.sort = this.sort;
          }
        },
        complete: () => {
          this.loading--;
        },
      }),
    );
  }
}
